import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Container,
  Heading,
  Input,
  VStack,
  HStack,
  Text,
  useToast,
  Card,
  CardBody,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  useColorModeValue,
  Select,
  Flex,
  SimpleGrid,
  Icon,
  Badge,
  Box,
  Center,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  ScaleFade,
  Tooltip,
  Progress,
} from '@chakra-ui/react';
import { FaPlus, FaTrash, FaBook, FaFire, FaHeart, FaStar, FaTrophy, FaBolt, FaLightbulb, FaBrain, FaClock, FaCheckCircle, FaCalendarAlt } from 'react-icons/fa';
import { FiHome, FiPlay, FiBarChart2, FiSettings, FiUser, FiBook, FiCheck, FiList, FiLayers, FiPlus, FiZap, FiTrendingUp } from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSRS, getUrgencyLevel, getUrgencyColor } from '../hooks/useSRS';
import { apiClient } from "../lib/apiClient";
import { useAuth } from "../contexts/AuthContext";
import {
  generateSentencesForWord,
  storeSentencesInDatabase,
} from "../services/tatoeba";
import { colors } from "../assets/styles/colors";
import { useTranslation } from "react-i18next";
import StatCard from "../components/dashboard/StatCard";
import ResponsiveTable from "../components/dashboard/ResponsiveTable";
import MasteryBadge from "../components/dashboard/MasteryBadge";
import TimeAgo from "../components/dashboard/TimeAgo";
import SentenceCountBadge from "../components/dashboard/SentenceCountBadge";
import ConfirmationDialog from "../components/common/ConfirmationDialog";
import logger from "../utils/logger";

// Add SRS imports

interface Word {
  id: string;
  word: string;
  language: string;
  created_at: string;
  sentence_count?: number;
  mastery?: number;
  last_practiced?: string;
  sentences?: SentenceBasic[];
}

interface SentenceBasic {
  id: string;
  remembered: boolean;
  word_id: string;
}

export default function Dashboard() {
  // Auth and navigation hooks
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // UI hooks
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();

  // ALL Theme value hooks - CONSOLIDATED to fix hooks order issue
  const cardBg = useColorModeValue("white", colors.primary[700]);
  const borderColor = useColorModeValue("gray.200", colors.primary[600]);
  const textColor = useColorModeValue("gray.500", "gray.300");
  const highlightColor = useColorModeValue("purple.500", "purple.300");
  const tableHeaderBg = useColorModeValue("gray.50", colors.primary[600]);
  const tableShadow = useColorModeValue("lg", "dark-lg");
  const tableContainerBg = useColorModeValue(
    "white",
    `linear-gradient(to bottom, ${colors.primary[700]}, ${colors.primary[800]})`
  );
  const activeTabBg = useColorModeValue("white", colors.primary[500]);
  const hoverShadow = useColorModeValue("xl", "2xl");
  const tabSelectedColor = useColorModeValue("brand.600", "white");
  const tabHoverBg = useColorModeValue("gray.100", colors.primary[600]);
  const wordCardBg = useColorModeValue("white", colors.primary[700]);
  const thColor = useColorModeValue("gray.600", "white");
  const wordHoverBg = useColorModeValue("gray.50", colors.primary[600]);
  const emptyStateIconColor = useColorModeValue("purple.500", "purple.300");
  // Additional color mode values that were scattered throughout the component
  const reviewCardBg = useColorModeValue("white", "gray.700");
  const reviewEmptyBg = useColorModeValue("white", "gray.700");
  const progressBg = useColorModeValue("gray.50", "gray.700");
  const urgencyGradientLight = useColorModeValue("50", "900");
  const urgencyGradientOpacity = useColorModeValue(0.1, 0.1);
  const reviewTextColor = useColorModeValue("gray.600", "gray.400");
  const reviewTimeColor = useColorModeValue("gray.500", "gray.400");
  const translationBg = useColorModeValue("gray.50", "gray.700");
  const translationBorder = useColorModeValue("purple.100", "purple.800");

  // Delete confirmation dialog state
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();
  const [wordToDelete, setWordToDelete] = useState<{
    id: string;
    word: string;
  } | null>(null);

  // Open dialog when wordToDelete is set
  useEffect(() => {
    if (wordToDelete) {
      onDeleteDialogOpen();
    }
  }, [wordToDelete, onDeleteDialogOpen]);

  // Handle dialog close
  const handleDeleteDialogClose = () => {
    setWordToDelete(null);
    onDeleteDialogClose();
  };

  // State for managing new word input and selected language
  const [newWord, setNewWord] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("english");
  const [isGeneratingSentences, setIsGeneratingSentences] = useState(false);
  const [activeLanguageTab, setActiveLanguageTab] = useState(0);
  const [hoverState, setHoverState] = useState<string | null>(null);

  // Query hooks - Make sure these are always in the same order
  const { data: userSettings } = useQuery({
    queryKey: ["user-settings"],
    queryFn: async () => {
      try {
        const data = await apiClient.getUserSettings();
        return data?.settings;
      } catch (error) {
        logger.error("Error fetching user settings:", error);
        return null;
      }
    },
    enabled: !!user,
  });

  const { data: words, isLoading } = useQuery({
    queryKey: ["words-with-practice"],
    queryFn: async () => {
      try {
        // First get all the words (now includes sentences)
        const wordsData = await apiClient.getWords();

        let playHistoryData = null;
        try {
          // Get the practice data from play_history
          playHistoryData = await apiClient.getPlayHistory();
        } catch (historyError) {
          logger.error("Exception fetching play history:", historyError);
        }

        let lessonReportsData = null;
        try {
          // Get the smart practice data from lesson_reports
          lessonReportsData = await apiClient.getLessonReports();
        } catch (reportsError) {
          logger.error("Exception fetching lesson reports:", reportsError);
        }

        // Create a map of word IDs to their latest practice dates
        const practiceMap: Record<string, string> = {};

        // Process play history
        if (playHistoryData && Array.isArray(playHistoryData)) {
          playHistoryData.forEach((entry: any) => {
            // Note: playHistory uses sentenceId. We need to map to words?
            // Dashboard previously assumed word_id was in play_history.
            // But with D1 schema, it is not.
            // However, we can use wordsData to map sentenceId -> wordId?
            // Or just skip this if we can't link it easily without N+1.
            // But we fetched sentences with words.
            // Actually, wordsData has sentences.

            // Reverse map: sentenceId -> wordId
            // This is expensive to build every time.

            // Ideally, we rely on lessonReports or srsReviews (which we don't fetch here yet).
            // But playHistory endpoint returns what's in DB.

            // If entry has word_id (from legacy), use it.
            if (entry.word_id && entry.last_practiced) {
              practiceMap[entry.word_id] = practiceMap[entry.word_id]
                ? new Date(practiceMap[entry.word_id]) >
                  new Date(entry.last_practiced)
                  ? practiceMap[entry.word_id]
                  : entry.last_practiced
                : entry.last_practiced;
            } else if (entry.sentenceId && entry.createdAt) {
              // Try to find word for this sentence
              // This is O(W*S) which is fine for small datasets but bad for large.
              // Let's iterate words and their sentences.
              for (const w of wordsData) {
                if (
                  w.sentences?.some(
                    (s: any) => String(s.id) === String(entry.sentenceId)
                  )
                ) {
                  const date = entry.createdAt;
                  practiceMap[w.id] = practiceMap[w.id]
                    ? new Date(practiceMap[w.id]) > new Date(date)
                      ? practiceMap[w.id]
                      : date
                    : date;
                  break;
                }
              }
            }
          });
        }

        // Process lesson reports
        if (lessonReportsData && Array.isArray(lessonReportsData)) {
          lessonReportsData.forEach((report: any) => {
            // report.word_ids might be in reportData if not column?
            // schema has word_ids? No, schema has reportData (JSON).
            // My profileRoutes endpoint parses reportData.
            // reportData usually contains wordIds.

            const wordIds = report.reportData?.wordIds || report.word_ids;

            if (wordIds && Array.isArray(wordIds) && report.createdAt) {
              wordIds.forEach((wordId: string) => {
                practiceMap[wordId] = practiceMap[wordId]
                  ? new Date(practiceMap[wordId]) > new Date(report.createdAt)
                    ? practiceMap[wordId]
                    : report.createdAt
                  : report.createdAt;
              });
            }
          });
        }

        // Combine the data
        return wordsData.map((word: any) => {
          // Calculate mastery based on remembered sentences
          const wordSentences = word.sentences || [];
          const totalSentences = wordSentences.length;
          const rememberedSentences = wordSentences.filter(
            (s: SentenceBasic) => s.remembered
          ).length;

          // Calculate mastery as proportion of remembered sentences (0 to 1)
          const mastery =
            totalSentences > 0 ? rememberedSentences / totalSentences : 0;

          return {
            ...word,
            sentence_count: totalSentences,
            mastery: mastery,
            last_practiced: practiceMap[word.id] || null,
          };
        }) as Word[];
      } catch (error) {
        logger.error("Error in words query:", error);
        return [];
      }
    },
    enabled: !!user,
  });

  const { data: streak } = useQuery({
    queryKey: ["user-streak"],
    queryFn: async () => {
      try {
        const stats = await apiClient.getUserStats();
        return {
          current_streak: stats.currentStreak || 0,
          max_streak: stats.longestStreak || 0,
        };
      } catch (err) {
        logger.error("Error fetching streak:", err);
        return { current_streak: 0, max_streak: 0 };
      }
    },
    enabled: !!user,
  });

  // SRS hooks
  const { useDueReviews, useReviewStats, scheduleBulkReviewMutation } =
    useSRS();
  const { data: dueReviews, isLoading: reviewsLoading } = useDueReviews(10);
  const { data: srsStats, isLoading: statsLoading } = useReviewStats();

  const { data: favorites } = useQuery({
    queryKey: ["favorites-count"],
    queryFn: async () => {
      try {
        const data = await apiClient.getFavorites();
        return data?.length || 0;
      } catch (err) {
        logger.error("Error fetching favorites:", err);
        return 0;
      }
    },
    enabled: !!user,
  });

  // Add a separate count query for words to get accurate count
  const { data: wordsCount } = useQuery({
    queryKey: ["words-count"],
    queryFn: async () => {
      try {
        const data = await apiClient.getWords();
        return data?.length || 0;
      } catch (err) {
        logger.error("Error fetching words count:", err);
        return 0;
      }
    },
    enabled: !!user,
  });

  // Get list of unique languages from words
  const availableLanguages = useMemo(() => {
    if (!words) return [];

    const languages = new Set<string>();
    words.forEach((word) => {
      if (word.language) {
        languages.add(word.language.toLowerCase());
      }
    });

    return Array.from(languages).map((lang) => ({
      value: lang.toLowerCase(),
      label: lang.charAt(0).toUpperCase() + lang.slice(1),
    }));
  }, [words]);

  // Effect to set the active tab to the user's target language
  useEffect(() => {
    if (userSettings?.targetLanguage && availableLanguages.length > 0) {
      const targetLang = userSettings.targetLanguage.toLowerCase();
      const targetLangIndex = availableLanguages.findIndex(
        (lang) => lang.value === targetLang
      );

      if (targetLangIndex >= 0) {
        setActiveLanguageTab(targetLangIndex);
      }

      setSelectedLanguage(targetLang);
    }
  }, [userSettings, availableLanguages]);

  // Filter words based on the active tab
  const filteredWordsByTab = useMemo(() => {
    if (!words || !availableLanguages[activeLanguageTab]) return [];

    const activeLanguage = availableLanguages[activeLanguageTab].value;
    return words.filter(
      (word) => word.language.toLowerCase() === activeLanguage
    );
  }, [words, availableLanguages, activeLanguageTab]);

  // Mutation hooks
  const addWord = useMutation({
    mutationFn: async (wordData: { word: string; language: string }) => {
      try {
        // Use apiClient to add the word
        const newWord = await apiClient.createWord(wordData.word, wordData.language);
        
        if (newWord && newWord.id) {
          const wordId = newWord.id;

          // Fetch sentences separately and store them
          try {
            const sentences = await generateSentencesForWord(
              wordData.word,
              wordData.language,
              userSettings?.nativeLanguage
            );

            if (sentences && sentences.length > 0) {
              await storeSentencesInDatabase(wordId, sentences, user?.id || "", wordData.language);
            }
          } catch (sentenceError) {
            setIsGeneratingSentences(false);
            logger.error("Error generating sentences:", sentenceError);
            toast({
              title: "Error",
              description: "Failed to generate sentences",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            return;
          }
        }

        return newWord;
      } catch (error) {
        logger.error("Error adding word:", error);
        toast({
          title: "Error",
          description: "Failed to add word",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words-with-practice"] });
      queryClient.invalidateQueries({ queryKey: ["sentences"] });
      setNewWord("");
      toast({
        title: "Word added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      onClose();
    },
    onError: (error) => {
      logger.error("Error in addWord mutation:", error);
      toast({
        title: "Error",
        description: "Failed to add word",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const deleteWordMutation = useMutation({
    mutationFn: async (wordId: string) => {
      await apiClient.deleteWord(wordId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words-with-practice"] });
      queryClient.invalidateQueries({ queryKey: ["sentences"] });
      toast({
        title: "Success",
        description: "Word deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const generateSentencesMutation = useMutation({
    mutationFn: async ({
      wordId,
      word,
      language,
    }: {
      wordId: string;
      word: string;
      language: string;
    }) => {
      const sentences = await generateSentencesForWord(
        word,
        language,
        userSettings?.nativeLanguage
      );

      if (sentences.length > 0) {
        await storeSentencesInDatabase(
          wordId,
          sentences,
          user?.id || "",
          language
        );
      } else {
        throw new Error("No sentences found for this word");
      }

      return sentences;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["words-with-practice"] });
      queryClient.invalidateQueries({ queryKey: ["sentences"] });
      toast({
        title: "Success",
        description: `Sentences generated for "${variables.word}"`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  // Event handlers
  const handleAddWord = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWord || !selectedLanguage) return;
    addWord.mutate({ word: newWord.trim(), language: selectedLanguage });
  };

  const handleTabChange = (index: number) => {
    setActiveLanguageTab(index);
  };

  const handleGenerateSentences = (
    wordId: string,
    word: string,
    language: string
  ) => {
    generateSentencesMutation.mutate({ wordId, word, language });
  };

  const handleScheduleAll = async () => {
    if (!dueReviews || dueReviews.length === 0) {
      toast({
        title: t("common.error"),
        description: "No reviews to schedule",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const sentenceIds = dueReviews.map((review) => review.id);
      await scheduleBulkReviewMutation.mutateAsync(sentenceIds);

      toast({
        title: t("srs.bulkScheduleSuccess"),
        description: t("srs.bulkScheduleDescription", {
          count: sentenceIds.length,
        }),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error ? error.message : t("srs.bulkScheduleError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Add these to handle the stats values
  const currentStreakValue = streak?.current_streak ?? 0;
  const maxStreakValue = streak?.max_streak ?? 0;
  const favoritesValue = favorites ?? 0;

  // Convert to primitive strings outside of JSX
  const currentStreakStr = String(currentStreakValue);
  const maxStreakStr = String(maxStreakValue);
  const favoritesStr = String(favoritesValue);

  // Early return for unauthenticated users
  if (!user) {
    return (
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6}>
          <Heading variant="gradient">Please log in</Heading>
          <Button colorScheme="brand" onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </VStack>
      </Container>
    );
  }

  // Define tableContainer object with the references to the variables above
  const tableContainer = {
    ".chakra-offset-slide": {
      transition: "all 0.3s ease",
      transform: "translateY(0)",
      opacity: 1,
    },
    '.css-7j4idw, [class*="css-7j4idw"]': {
      borderRadius: "xl",
      boxShadow: tableShadow,
      overflow: "hidden",
      transition: "all 0.3s ease",
      bg: tableContainerBg,
      borderWidth: "1px",
      borderColor: borderColor,
      _hover: {
        boxShadow: hoverShadow,
        transform: "translateY(-4px)",
      },
    },
  };

  return (
    <Container maxW={{ base: "container.sm", md: "container.xl" }} py={10}>
      <VStack spacing={8} align="stretch">
        <Flex
          justify="space-between"
          align={{ base: "flex-start", md: "flex-end" }}
          flexWrap="wrap"
          gap={4}
        >
          <Heading
            as="h1"
            size={{ base: "lg", md: "xl" }}
            mb={2}
            bgGradient="linear(to-l, #7928CA, #FF0080)"
            bgClip="text"
            fontWeight="extrabold"
            flex="1"
            minW={{ base: "200px", md: "auto" }}
          >
            {t("common.dashboard")}
          </Heading>
          <HStack
            spacing={3}
            flexWrap={{ base: "wrap", sm: "nowrap" }}
            w={{ base: "100%", md: "auto" }}
          >
            <Button
              colorScheme="brand"
              variant="gradient"
              leftIcon={<Icon as={FiPlus} />}
              onClick={onOpen}
              boxShadow="md"
              _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
              transition="all 0.2s"
              size={{ base: "sm", md: "md" }}
              flex={{ base: "1", sm: "none" }}
              minW={{ base: "120px", sm: "auto" }}
            >
              {t("words.addWord")}
            </Button>
            <Button
              colorScheme="purple"
              variant="gradient"
              leftIcon={<Icon as={FiZap} />}
              onClick={() => navigate("/play")}
              boxShadow="md"
              _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
              transition="all 0.2s"
              size={{ base: "sm", md: "md" }}
              flex={{ base: "1", sm: "none" }}
              minW={{ base: "120px", sm: "auto" }}
            >
              {t("srs.startReviewing")}
            </Button>
          </HStack>
        </Flex>

        {isLoading ? (
          <Center p={10}>
            <VStack>
              <Spinner
                size="xl"
                color="brand.500"
                thickness="4px"
                speed="0.65s"
              />
              <Text mt={4} color={textColor} fontWeight="medium">
                {t("common.loading")}
              </Text>
            </VStack>
          </Center>
        ) : (
          <ScaleFade in={!isLoading} initialScale={0.9}>
            <VStack spacing={8} align="stretch">
              <Card
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="xl"
                boxShadow="md"
                overflow="hidden"
                mb={0}
                position="relative"
                _hover={{
                  boxShadow: "lg",
                  transform: "translateY(-2px)",
                }}
                transition="all 0.3s ease"
              >
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  height="4px"
                  bgGradient="linear(to-r, blue.400, purple.500, red.400)"
                />
                <Flex
                  w="full"
                  bg={cardBg}
                  p={{ base: 3, md: 5 }}
                  pt={{ base: 4, md: 6 }}
                  align="center"
                  justify="space-between"
                  flexWrap={{ base: "wrap", md: "nowrap" }}
                  gap={{ base: 2, md: 0 }}
                  flexDirection={{ base: "column", md: "row" }}
                >
                  {/* Mobile layout - stacked vertically */}
                  <VStack
                    spacing={3}
                    w="full"
                    display={{ base: "flex", md: "none" }}
                  >
                    <SimpleGrid columns={2} spacing={4} w="full">
                      <Box
                        textAlign="center"
                        p={3}
                        bg={progressBg}
                        borderRadius="md"
                      >
                        <Icon as={FiBook} color="blue.500" boxSize={5} mb={1} />
                        <Text
                          fontSize="xs"
                          color={textColor}
                          fontWeight="medium"
                        >
                          {t("dashboard.totalWords")}
                        </Text>
                        <Text
                          fontWeight="extrabold"
                          fontSize="lg"
                          bgGradient="linear(to-r, blue.400, blue.600)"
                          bgClip="text"
                        >
                          {wordsCount ?? words?.length ?? 0}
                        </Text>
                      </Box>

                      <Box
                        textAlign="center"
                        p={3}
                        bg={progressBg}
                        borderRadius="md"
                      >
                        <Icon
                          as={FaFire}
                          color="orange.400"
                          boxSize={5}
                          mb={1}
                        />
                        <Text
                          fontSize="xs"
                          color={textColor}
                          fontWeight="medium"
                        >
                          {t("dashboard.currentStreak")}
                        </Text>
                        <Text
                          fontWeight="extrabold"
                          fontSize="lg"
                          bgGradient="linear(to-r, orange.400, orange.600)"
                          bgClip="text"
                        >
                          {currentStreakValue}
                        </Text>
                      </Box>

                      <Box
                        textAlign="center"
                        p={3}
                        bg={progressBg}
                        borderRadius="md"
                      >
                        <Icon
                          as={FaTrophy}
                          color="yellow.500"
                          boxSize={5}
                          mb={1}
                        />
                        <Text
                          fontSize="xs"
                          color={textColor}
                          fontWeight="medium"
                        >
                          {t("dashboard.maxStreak")}
                        </Text>
                        <Text
                          fontWeight="extrabold"
                          fontSize="lg"
                          bgGradient="linear(to-r, yellow.400, yellow.600)"
                          bgClip="text"
                        >
                          {maxStreakValue}
                        </Text>
                      </Box>

                      <Box
                        textAlign="center"
                        p={3}
                        bg={progressBg}
                        borderRadius="md"
                      >
                        <Icon as={FaHeart} color="red.500" boxSize={5} mb={1} />
                        <Text
                          fontSize="xs"
                          color={textColor}
                          fontWeight="medium"
                        >
                          {t("dashboard.favorites")}
                        </Text>
                        <Text
                          fontWeight="extrabold"
                          fontSize="lg"
                          bgGradient="linear(to-r, red.400, red.600)"
                          bgClip="text"
                        >
                          {favoritesValue}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Box
                      textAlign="center"
                      p={3}
                      bg={progressBg}
                      borderRadius="md"
                      w="full"
                    >
                      <Icon as={FiCheck} color="green.500" boxSize={5} mb={1} />
                      <Text fontSize="xs" color={textColor} fontWeight="medium">
                        {t("dashboard.masteredWords")}
                      </Text>
                      <Text
                        fontWeight="extrabold"
                        fontSize="lg"
                        bgGradient="linear(to-r, green.400, green.600)"
                        bgClip="text"
                      >
                        {words?.filter((w) => (w.mastery ?? 0) >= 0.9).length ||
                          0}
                      </Text>
                    </Box>
                  </VStack>

                  {/* Desktop layout - horizontal with dividers */}
                  <HStack
                    spacing={4}
                    flex={{ base: "100%", md: 1 }}
                    display={{ base: "none", md: "flex" }}
                  >
                    <Icon as={FiBook} color="blue.500" boxSize={6} />
                    <Box>
                      <Text fontSize="xs" color={textColor} fontWeight="medium">
                        {t("dashboard.totalWords")}
                      </Text>
                      <Text
                        fontWeight="extrabold"
                        fontSize="xl"
                        bgGradient="linear(to-r, blue.400, blue.600)"
                        bgClip="text"
                      >
                        {wordsCount ?? words?.length ?? 0}
                      </Text>
                    </Box>
                  </HStack>

                  <Divider
                    orientation="vertical"
                    h="40px"
                    display={{ base: "none", md: "block" }}
                  />

                  <HStack
                    spacing={4}
                    flex={{ base: "1", md: 1 }}
                    _hover={{ transform: "scale(1.05)" }}
                    transition="transform 0.2s"
                    display={{ base: "none", md: "flex" }}
                  >
                    <Icon as={FaFire} color="orange.400" boxSize={6} />
                    <Box>
                      <Text fontSize="xs" color={textColor} fontWeight="medium">
                        {t("dashboard.currentStreak")}
                      </Text>
                      <Text
                        fontWeight="extrabold"
                        fontSize="xl"
                        bgGradient="linear(to-r, orange.400, orange.600)"
                        bgClip="text"
                      >
                        {currentStreakValue}
                      </Text>
                    </Box>
                  </HStack>

                  <Divider
                    orientation="vertical"
                    h="40px"
                    display={{ base: "none", md: "block" }}
                  />

                  <HStack
                    spacing={4}
                    flex={{ base: "1", md: 1 }}
                    _hover={{ transform: "scale(1.05)" }}
                    transition="transform 0.2s"
                    display={{ base: "none", md: "flex" }}
                  >
                    <Icon as={FaTrophy} color="yellow.500" boxSize={6} />
                    <Box>
                      <Text fontSize="xs" color={textColor} fontWeight="medium">
                        {t("dashboard.maxStreak")}
                      </Text>
                      <Text
                        fontWeight="extrabold"
                        fontSize="xl"
                        bgGradient="linear(to-r, yellow.400, yellow.600)"
                        bgClip="text"
                      >
                        {maxStreakValue}
                      </Text>
                    </Box>
                  </HStack>

                  <Divider
                    orientation="vertical"
                    h="40px"
                    display={{ base: "none", md: "block" }}
                  />

                  <HStack
                    spacing={4}
                    flex={{ base: "1", md: 1 }}
                    _hover={{ transform: "scale(1.05)" }}
                    transition="transform 0.2s"
                    display={{ base: "none", md: "flex" }}
                  >
                    <Icon as={FaHeart} color="red.500" boxSize={6} />
                    <Box>
                      <Text fontSize="xs" color={textColor} fontWeight="medium">
                        {t("dashboard.favorites")}
                      </Text>
                      <Text
                        fontWeight="extrabold"
                        fontSize="xl"
                        bgGradient="linear(to-r, red.400, red.600)"
                        bgClip="text"
                      >
                        {favoritesValue}
                      </Text>
                    </Box>
                  </HStack>

                  <Divider
                    orientation="vertical"
                    h="40px"
                    display={{ base: "none", md: "block" }}
                  />

                  <HStack
                    spacing={4}
                    flex={{ base: "100%", md: 1 }}
                    _hover={{ transform: "scale(1.05)" }}
                    transition="transform 0.2s"
                    display={{ base: "none", md: "flex" }}
                  >
                    <Icon as={FiCheck} color="green.500" boxSize={6} />
                    <Box>
                      <Text fontSize="xs" color={textColor} fontWeight="medium">
                        {t("dashboard.masteredWords")}
                      </Text>
                      <Text
                        fontWeight="extrabold"
                        fontSize="xl"
                        bgGradient="linear(to-r, green.400, green.600)"
                        bgClip="text"
                      >
                        {words?.filter((w) => (w.mastery ?? 0) >= 0.9).length ||
                          0}
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </Card>

              {/* SRS Components Section */}
              <VStack spacing={6} align="stretch">
                {/* Integrated SRS Dashboard */}
                <Card
                  bg={cardBg}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="xl"
                  boxShadow="lg"
                  overflow="hidden"
                  position="relative"
                  _hover={{
                    boxShadow: "xl",
                    transform: "translateY(-2px)",
                  }}
                  transition="all 0.3s ease"
                >
                  {/* Gradient header bar */}
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    height="4px"
                    bgGradient="linear(to-r, purple.400, blue.400, teal.400)"
                  />

                  <CardBody p={6} pt={8}>
                    <VStack spacing={6} align="stretch">
                      {/* Header with title */}
                      <Flex
                        justify="space-between"
                        align="center"
                        flexWrap="wrap"
                        gap={4}
                      >
                        <VStack align="start" spacing={1} flex={1}>
                          <HStack spacing={3}>
                            <Icon
                              as={FaBrain}
                              color="purple.500"
                              boxSize={{ base: 5, md: 6 }}
                            />
                            <Heading
                              size={{ base: "md", md: "lg" }}
                              color="purple.600"
                            >
                              {t("srs.reviewQueue")}
                            </Heading>
                          </HStack>
                          <Text
                            fontSize={{ base: "xs", md: "sm" }}
                            color={textColor}
                          >
                            {t("srs.smartSpacedRepetition")}
                          </Text>
                        </VStack>
                      </Flex>

                      {/* Main SRS Stats Grid - Consolidated */}
                      {statsLoading ? (
                        <Center>
                          <Spinner color="purple.500" />
                        </Center>
                      ) : (
                        <SimpleGrid
                          columns={{ base: 2, sm: 2, md: 3, lg: 6 }}
                          spacing={{ base: 2, md: 3, lg: 4 }}
                        >
                          {/* Overdue - Highest Priority */}
                          <Card
                            bg="red.50"
                            borderWidth="2px"
                            borderColor="red.200"
                            borderRadius="lg"
                            _dark={{ bg: "red.900", borderColor: "red.700" }}
                            minH={{ base: "100px", md: "120px" }}
                          >
                            <CardBody
                              textAlign="center"
                              py={{ base: 2, md: 4 }}
                              px={{ base: 2, md: 4 }}
                            >
                              <Icon
                                as={FaFire}
                                color="red.500"
                                boxSize={{ base: 4, md: 6, lg: 8 }}
                                mb={1}
                              />
                              <Text
                                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                                fontWeight="bold"
                                color="red.500"
                              >
                                {srsStats?.overdueCount || 0}
                              </Text>
                              <Text
                                fontSize={{ base: "2xs", md: "xs" }}
                                color="red.600"
                                fontWeight="medium"
                              >
                                {t("srs.overdue")}
                              </Text>
                            </CardBody>
                          </Card>

                          {/* Due Today */}
                          <Card
                            bg="orange.50"
                            borderWidth="2px"
                            borderColor="orange.200"
                            borderRadius="lg"
                            _dark={{
                              bg: "orange.900",
                              borderColor: "orange.700",
                            }}
                            minH={{ base: "100px", md: "120px" }}
                          >
                            <CardBody
                              textAlign="center"
                              py={{ base: 2, md: 4 }}
                              px={{ base: 2, md: 4 }}
                            >
                              <Icon
                                as={FaClock}
                                color="orange.500"
                                boxSize={{ base: 4, md: 6, lg: 8 }}
                                mb={1}
                              />
                              <Text
                                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                                fontWeight="bold"
                                color="orange.500"
                              >
                                {srsStats?.dueCount || 0}
                              </Text>
                              <Text
                                fontSize={{ base: "2xs", md: "xs" }}
                                color="orange.600"
                                fontWeight="medium"
                              >
                                {t("srs.dueToday")}
                              </Text>
                            </CardBody>
                          </Card>

                          {/* Completed Today */}
                          <Card
                            bg="green.50"
                            borderWidth="2px"
                            borderColor="green.200"
                            borderRadius="lg"
                            _dark={{
                              bg: "green.900",
                              borderColor: "green.700",
                            }}
                            minH={{ base: "100px", md: "120px" }}
                          >
                            <CardBody
                              textAlign="center"
                              py={{ base: 2, md: 4 }}
                              px={{ base: 2, md: 4 }}
                            >
                              <Icon
                                as={FaCheckCircle}
                                color="green.500"
                                boxSize={{ base: 4, md: 6, lg: 8 }}
                                mb={1}
                              />
                              <Text
                                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                                fontWeight="bold"
                                color="green.500"
                              >
                                {srsStats?.reviewsToday || 0}
                              </Text>
                              <Text
                                fontSize={{ base: "2xs", md: "xs" }}
                                color="green.600"
                                fontWeight="medium"
                              >
                                {t("srs.completed")}
                              </Text>
                            </CardBody>
                          </Card>

                          {/* Average Difficulty Grade */}
                          <Card
                            bg="purple.50"
                            borderWidth="2px"
                            borderColor="purple.200"
                            borderRadius="lg"
                            _dark={{
                              bg: "purple.900",
                              borderColor: "purple.700",
                            }}
                            minH={{ base: "100px", md: "120px" }}
                          >
                            <CardBody
                              textAlign="center"
                              py={{ base: 2, md: 4 }}
                              px={{ base: 2, md: 4 }}
                            >
                              <Icon
                                as={FaTrophy}
                                color="purple.500"
                                boxSize={{ base: 4, md: 6, lg: 8 }}
                                mb={1}
                              />
                              <Text
                                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                                fontWeight="bold"
                                color="purple.500"
                              >
                                {srsStats?.averageDifficulty
                                  ? srsStats.averageDifficulty.toFixed(1)
                                  : "0.0"}
                              </Text>
                              <Text
                                fontSize={{ base: "2xs", md: "xs" }}
                                color="purple.600"
                                fontWeight="medium"
                              >
                                {t("srs.grade")}
                              </Text>
                            </CardBody>
                          </Card>

                          {/* Mature Cards */}
                          <Card
                            bg="blue.50"
                            borderWidth="2px"
                            borderColor="blue.200"
                            borderRadius="lg"
                            _dark={{ bg: "blue.900", borderColor: "blue.700" }}
                            minH={{ base: "100px", md: "120px" }}
                          >
                            <CardBody
                              textAlign="center"
                              py={{ base: 2, md: 4 }}
                              px={{ base: 2, md: 4 }}
                            >
                              <Icon
                                as={FaBolt}
                                color="blue.500"
                                boxSize={{ base: 4, md: 6, lg: 8 }}
                                mb={1}
                              />
                              <Text
                                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                                fontWeight="bold"
                                color="blue.500"
                              >
                                {srsStats?.matureCount || 0}
                              </Text>
                              <Text
                                fontSize={{ base: "2xs", md: "xs" }}
                                color="blue.600"
                                fontWeight="medium"
                              >
                                {t("srs.mature")}
                              </Text>
                            </CardBody>
                          </Card>

                          {/* Retention Rate */}
                          <Card
                            bg="teal.50"
                            borderWidth="2px"
                            borderColor="teal.200"
                            borderRadius="lg"
                            _dark={{ bg: "teal.900", borderColor: "teal.700" }}
                            minH={{ base: "100px", md: "120px" }}
                          >
                            <CardBody
                              textAlign="center"
                              py={{ base: 2, md: 4 }}
                              px={{ base: 2, md: 4 }}
                            >
                              <Icon
                                as={FiTrendingUp}
                                color="teal.500"
                                boxSize={{ base: 4, md: 6, lg: 8 }}
                                mb={1}
                              />
                              <Text
                                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                                fontWeight="bold"
                                color="teal.500"
                              >
                                {srsStats?.retentionRate
                                  ? `${srsStats.retentionRate.toFixed(0)}%`
                                  : "0%"}
                              </Text>
                              <Text
                                fontSize={{ base: "2xs", md: "xs" }}
                                color="teal.600"
                                fontWeight="medium"
                              >
                                {t("srs.retentionRate")}
                              </Text>
                            </CardBody>
                          </Card>
                        </SimpleGrid>
                      )}

                      {/* Progress Section */}
                      {srsStats && srsStats.dueCount > 0 && (
                        <Box
                          bg={progressBg}
                          borderRadius="lg"
                          p={{ base: 3, md: 4 }}
                        >
                          <VStack spacing={3}>
                            <HStack justify="space-between" w="full">
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={textColor}
                              >
                                {t("srs.dailyProgress")}
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="purple.500"
                              >
                                {srsStats.reviewsToday} / {srsStats.dueCount}{" "}
                                {t("srs.reviews")}
                              </Text>
                            </HStack>
                            <Progress
                              value={
                                srsStats.dueCount > 0
                                  ? (srsStats.reviewsToday /
                                      srsStats.dueCount) *
                                    100
                                  : 0
                              }
                              colorScheme="purple"
                              size="lg"
                              rounded="full"
                              w="full"
                            />
                            <HStack
                              justify="space-between"
                              w="full"
                              fontSize="xs"
                              color={textColor}
                            >
                              <Text>
                                {srsStats.reviewsToday === 0
                                  ? t("srs.keepGoing")
                                  : t("srs.almostDone")}
                              </Text>
                              <Text>
                                {srsStats.reviewsToday >= srsStats.dueCount
                                  ? "🎉"
                                  : "📚"}
                              </Text>
                            </HStack>
                          </VStack>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      <HStack
                        spacing={{ base: 2, md: 4 }}
                        justify="center"
                        flexWrap={{ base: "wrap", sm: "nowrap" }}
                        w="full"
                      >
                        <Button
                          colorScheme="blue"
                          variant="outline"
                          leftIcon={<Icon as={FiBarChart2} />}
                          onClick={() => navigate("/analytics")}
                          size={{ base: "sm", md: "lg" }}
                          flex={{ base: "1", sm: "none" }}
                          minW={{ base: "120px", sm: "auto" }}
                        >
                          {t("srs.viewAnalytics")}
                        </Button>

                        <Button
                          colorScheme="gray"
                          variant="outline"
                          leftIcon={<Icon as={FaCalendarAlt} />}
                          onClick={handleScheduleAll}
                          isDisabled={!dueReviews || dueReviews.length === 0}
                          isLoading={scheduleBulkReviewMutation.isPending}
                          size={{ base: "sm", md: "lg" }}
                          flex={{ base: "1", sm: "none" }}
                          minW={{ base: "120px", sm: "auto" }}
                        >
                          {t("srs.scheduleAll")}
                        </Button>
                      </HStack>

                      {/* Upcoming Reviews Preview - Redesigned */}
                      <Box>
                        <Flex justify="space-between" align="center" mb={6}>
                          <HStack spacing={3}>
                            <Icon
                              as={FaCalendarAlt}
                              color="purple.500"
                              boxSize={5}
                            />
                            <Heading size="md" color="purple.600">
                              {t("srs.upcomingReviews")}
                            </Heading>
                          </HStack>
                          {dueReviews && dueReviews.length > 0 && (
                            <Badge
                              colorScheme="purple"
                              variant="subtle"
                              px={3}
                              py={1}
                              borderRadius="full"
                              fontSize="sm"
                              fontWeight="medium"
                            >
                              {dueReviews.length}{" "}
                              {dueReviews.length === 1 ? "item" : "items"}
                            </Badge>
                          )}
                        </Flex>

                        {reviewsLoading ? (
                          <Center py={12}>
                            <VStack spacing={4}>
                              <Spinner color="purple.500" size="lg" />
                              <Text fontSize="sm" color={textColor}>
                                Loading reviews...
                              </Text>
                            </VStack>
                          </Center>
                        ) : dueReviews && dueReviews.length > 0 ? (
                          <VStack spacing={4} maxH="500px" overflowY="auto">
                            {dueReviews.slice(0, 5).map((review) => {
                              const urgency = getUrgencyLevel(
                                review.srs_due_date
                              );
                              const urgencyColor = getUrgencyColor(urgency);

                              const formatTimeUntilDue = (
                                dueDate: string
                              ): string => {
                                const due = new Date(dueDate);
                                const now = new Date();
                                const diffMs = due.getTime() - now.getTime();
                                const diffHours =
                                  Math.abs(diffMs) / (1000 * 60 * 60);
                                const diffDays = Math.floor(diffHours / 24);

                                if (diffMs < 0) {
                                  if (diffDays === 0) {
                                    return t("srs.overdueHours", {
                                      hours: Math.floor(diffHours),
                                    });
                                  }
                                  return t("srs.overdueDays", {
                                    days: diffDays,
                                  });
                                } else {
                                  if (diffDays === 0) {
                                    return t("srs.dueInHours", {
                                      hours: Math.floor(diffHours),
                                    });
                                  }
                                  return t("srs.dueInDays", { days: diffDays });
                                }
                              };

                              const getUrgencyIcon = (urgency: string) => {
                                switch (urgency) {
                                  case "overdue":
                                    return FaFire;
                                  case "due":
                                    return FaClock;
                                  default:
                                    return FaCheckCircle;
                                }
                              };

                              return (
                                <Card
                                  key={review.id}
                                  w="full"
                                  bg={reviewCardBg}
                                  borderWidth="1px"
                                  borderColor={borderColor}
                                  borderRadius="lg"
                                  borderLeftWidth="4px"
                                  borderLeftColor={urgencyColor}
                                  boxShadow="sm"
                                  _hover={{
                                    boxShadow: "md",
                                    transform: "translateY(-1px)",
                                    borderColor: urgencyColor,
                                  }}
                                  transition="all 0.2s ease-in-out"
                                >
                                  <CardBody p={4}>
                                    <Flex
                                      justify="space-between"
                                      align="flex-start"
                                      gap={4}
                                    >
                                      {/* Main content */}
                                      <VStack
                                        align="start"
                                        spacing={3}
                                        flex={1}
                                        minW={0}
                                      >
                                        {/* Sentence */}
                                        <Text
                                          fontWeight="semibold"
                                          fontSize="md"
                                          color={textColor}
                                          lineHeight="1.4"
                                          noOfLines={2}
                                          wordBreak="break-word"
                                        >
                                          {review.sentence}
                                        </Text>

                                        {/* Translation */}
                                        <Text
                                          fontSize="sm"
                                          color={reviewTextColor}
                                          fontStyle="italic"
                                          lineHeight="1.3"
                                          noOfLines={1}
                                          wordBreak="break-word"
                                        >
                                          "{review.translation}"
                                        </Text>

                                        {/* Word and Language badges */}
                                        <HStack spacing={2} flexWrap="wrap">
                                          <Badge
                                            colorScheme="purple"
                                            variant="subtle"
                                            borderRadius="full"
                                            px={3}
                                            py={1}
                                            fontSize="xs"
                                          >
                                            {review.words.word}
                                          </Badge>
                                          <Badge
                                            colorScheme="blue"
                                            variant="outline"
                                            borderRadius="full"
                                            px={3}
                                            py={1}
                                            fontSize="xs"
                                          >
                                            {review.words.language}
                                          </Badge>
                                        </HStack>
                                      </VStack>

                                      {/* Urgency indicator */}
                                      <VStack
                                        align="end"
                                        spacing={2}
                                        flexShrink={0}
                                      >
                                        <HStack spacing={2}>
                                          <Icon
                                            as={getUrgencyIcon(urgency)}
                                            color={urgencyColor}
                                            boxSize={4}
                                          />
                                          <Badge
                                            colorScheme={
                                              urgencyColor.split(".")[0]
                                            }
                                            variant="solid"
                                            borderRadius="full"
                                            fontSize="xs"
                                            px={2}
                                            py={1}
                                          >
                                            {t(`srs.${urgency}`)}
                                          </Badge>
                                        </HStack>
                                        <Text
                                          fontSize="xs"
                                          color={urgencyColor}
                                          fontWeight="medium"
                                          textAlign="right"
                                        >
                                          {formatTimeUntilDue(
                                            review.srs_due_date
                                          )}
                                        </Text>
                                        <Text
                                          fontSize="xs"
                                          color={reviewTimeColor}
                                          textAlign="right"
                                        >
                                          {t("srs.interval")}:{" "}
                                          {review.srs_interval}d
                                        </Text>
                                      </VStack>
                                    </Flex>
                                  </CardBody>
                                </Card>
                              );
                            })}

                            {dueReviews.length > 5 && (
                              <Box
                                py={3}
                                px={4}
                                bg={progressBg}
                                borderRadius="lg"
                                borderWidth="1px"
                                borderColor={borderColor}
                                borderStyle="dashed"
                                w="full"
                              >
                                <Text
                                  fontSize="sm"
                                  color={textColor}
                                  textAlign="center"
                                  fontWeight="medium"
                                >
                                  {t("srs.andMoreReviews", {
                                    count: dueReviews.length - 5,
                                  })}
                                </Text>
                              </Box>
                            )}
                          </VStack>
                        ) : (
                          <Card
                            bg={reviewEmptyBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                            borderRadius="xl"
                            overflow="hidden"
                          >
                            <CardBody textAlign="center" py={12}>
                              <VStack spacing={4}>
                                <Box bg={progressBg} borderRadius="full" p={4}>
                                  <Icon
                                    as={FaCheckCircle}
                                    color="green.500"
                                    boxSize={10}
                                  />
                                </Box>
                                <VStack spacing={2}>
                                  <Heading
                                    size="md"
                                    color="green.500"
                                    fontWeight="bold"
                                  >
                                    {t("srs.allCaughtUp")}
                                  </Heading>
                                  <Text
                                    color={textColor}
                                    fontSize="sm"
                                    maxW="250px"
                                  >
                                    {t("srs.noReviewsDue")}
                                  </Text>
                                </VStack>
                              </VStack>
                            </CardBody>
                          </Card>
                        )}
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>

              <Card
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="xl"
                boxShadow="lg"
                overflow="hidden"
                sx={tableContainer}
              >
                <Box bg={highlightColor} h="8px" w="full" />
                <CardBody p={{ base: 4, md: 6 }}>
                  {availableLanguages.length > 0 ? (
                    <Tabs
                      variant="soft-rounded"
                      colorScheme="brand"
                      index={activeLanguageTab}
                      onChange={handleTabChange}
                      mb={4}
                    >
                      <TabList
                        mb={6}
                        overflowX="auto"
                        py={2}
                        css={{
                          scrollbarWidth: "none",
                          "::-webkit-scrollbar": { display: "none" },
                          gap: "8px",
                          display: "flex",
                        }}
                        sx={{
                          "@media (max-width: 768px)": {
                            flexWrap: "nowrap",
                            paddingBottom: "10px",
                          },
                          "@media (min-width: 769px)": {
                            flexWrap: "wrap",
                          },
                        }}
                      >
                        {availableLanguages.map((lang, index) => (
                          <Tab
                            key={lang.value}
                            py={2}
                            px={4}
                            borderRadius="full"
                            fontWeight="medium"
                            _selected={{
                              bg: activeTabBg,
                              color: tabSelectedColor,
                              fontWeight: "bold",
                              boxShadow: "md",
                            }}
                            _hover={{
                              bg: tabHoverBg,
                              transform: "translateY(-2px)",
                            }}
                            transition="all 0.2s"
                          >
                            {lang.label}
                          </Tab>
                        ))}
                      </TabList>

                      <TabPanels>
                        {availableLanguages.map((lang, index) => (
                          <TabPanel key={lang.value} p={0}>
                            <Box
                              borderRadius="lg"
                              overflow="hidden"
                              boxShadow="sm"
                              borderWidth="1px"
                              borderColor={borderColor}
                              bg={wordCardBg}
                              transition="all 0.2s"
                            >
                              <ResponsiveTable>
                                <Thead bg={tableHeaderBg}>
                                  <Tr>
                                    <Th
                                      color={thColor}
                                      fontSize="xs"
                                      fontWeight="bold"
                                      textTransform="uppercase"
                                      letterSpacing="wider"
                                    >
                                      {t("common.word")}
                                    </Th>
                                    <Th
                                      color={thColor}
                                      fontSize="xs"
                                      fontWeight="bold"
                                      textTransform="uppercase"
                                      letterSpacing="wider"
                                      display={{
                                        base: "none",
                                        md: "table-cell",
                                      }}
                                    >
                                      {t("common.mastery")}
                                    </Th>
                                    <Th
                                      color={thColor}
                                      fontSize="xs"
                                      fontWeight="bold"
                                      textTransform="uppercase"
                                      letterSpacing="wider"
                                      display={{
                                        base: "none",
                                        lg: "table-cell",
                                      }}
                                    >
                                      {t("common.lastPracticed")}
                                    </Th>
                                    <Th></Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {filteredWordsByTab.length > 0 ? (
                                    filteredWordsByTab
                                      .slice(0, 5)
                                      .map((word) => (
                                        <Tr
                                          key={word.id}
                                          bg={
                                            hoverState === word.id
                                              ? wordHoverBg
                                              : "transparent"
                                          }
                                          transition="all 0.3s"
                                          onMouseEnter={() =>
                                            setHoverState(word.id)
                                          }
                                          onMouseLeave={() =>
                                            setHoverState(null)
                                          }
                                          borderRadius="md"
                                          _hover={{
                                            boxShadow: "md",
                                            transform: "scale(1.01)",
                                          }}
                                        >
                                          <Td fontWeight="bold">
                                            <Text
                                              fontWeight="bold"
                                              bgGradient={
                                                hoverState === word.id
                                                  ? "linear(to-l, #7928CA, #FF0080)"
                                                  : "none"
                                              }
                                              bgClip={
                                                hoverState === word.id
                                                  ? "text"
                                                  : "none"
                                              }
                                              transition="all 0.3s"
                                              fontSize="md"
                                            >
                                              {word.word}
                                            </Text>
                                            <SentenceCountBadge
                                              count={word.sentence_count}
                                            />
                                          </Td>
                                          <Td
                                            display={{
                                              base: "none",
                                              md: "table-cell",
                                            }}
                                          >
                                            <MasteryBadge
                                              mastery={word.mastery || 0}
                                            />
                                          </Td>
                                          <Td
                                            display={{
                                              base: "none",
                                              lg: "table-cell",
                                            }}
                                          >
                                            {word.last_practiced ? (
                                              <TimeAgo
                                                date={word.last_practiced}
                                              />
                                            ) : (
                                              <Text
                                                color={textColor}
                                                fontStyle="italic"
                                              >
                                                {t("common.never")}
                                              </Text>
                                            )}
                                          </Td>
                                          <Td>
                                            <HStack
                                              spacing={{ base: 1, md: 2 }}
                                              justifyContent="flex-end"
                                            >
                                              <Tooltip
                                                label={t("words.viewSentences")}
                                                hasArrow
                                                placement="top"
                                              >
                                                <IconButton
                                                  size={{
                                                    base: "xs",
                                                    md: "sm",
                                                  }}
                                                  colorScheme="blue"
                                                  icon={<FaBook />}
                                                  aria-label={t(
                                                    "words.viewSentences"
                                                  )}
                                                  onClick={() =>
                                                    navigate(
                                                      `/words/${word.id}`
                                                    )
                                                  }
                                                  borderRadius="md"
                                                  boxShadow="sm"
                                                  _hover={{
                                                    transform:
                                                      "translateY(-2px)",
                                                    boxShadow: "md",
                                                  }}
                                                  transition="all 0.2s"
                                                />
                                              </Tooltip>
                                              <Tooltip
                                                label={t("words.deleteWord")}
                                                hasArrow
                                                placement="top"
                                              >
                                                <IconButton
                                                  size={{
                                                    base: "xs",
                                                    md: "sm",
                                                  }}
                                                  colorScheme="red"
                                                  icon={<FaTrash />}
                                                  aria-label={t(
                                                    "words.deleteWord"
                                                  )}
                                                  onClick={() =>
                                                    setWordToDelete({
                                                      id: word.id,
                                                      word: word.word,
                                                    })
                                                  }
                                                  borderRadius="md"
                                                  boxShadow="sm"
                                                  _hover={{
                                                    transform:
                                                      "translateY(-2px)",
                                                    boxShadow: "md",
                                                  }}
                                                  transition="all 0.2s"
                                                />
                                              </Tooltip>
                                            </HStack>
                                          </Td>
                                        </Tr>
                                      ))
                                  ) : (
                                    <Tr>
                                      <Td colSpan={4} textAlign="center" py={8}>
                                        <VStack spacing={3}>
                                          <Icon
                                            as={FaLightbulb}
                                            boxSize={8}
                                            color={emptyStateIconColor}
                                          />
                                          <Text>
                                            {t("dashboard.noWordsInLanguage", {
                                              language: lang.label,
                                            })}
                                          </Text>
                                          <Button
                                            size="sm"
                                            leftIcon={<FaPlus />}
                                            colorScheme="brand"
                                            onClick={onOpen}
                                            variant="outline"
                                            _hover={{
                                              transform: "translateY(-2px)",
                                              boxShadow: "md",
                                            }}
                                            transition="all 0.2s"
                                          >
                                            {t("words.addWord")}
                                          </Button>
                                        </VStack>
                                      </Td>
                                    </Tr>
                                  )}
                                </Tbody>
                              </ResponsiveTable>
                            </Box>
                          </TabPanel>
                        ))}
                      </TabPanels>
                    </Tabs>
                  ) : (
                    <Box textAlign="center" py={10}>
                      <VStack spacing={6}>
                        <Icon
                          as={FiBook}
                          boxSize={12}
                          color={emptyStateIconColor}
                        />
                        <Heading size="md">{t("dashboard.noWordsYet")}</Heading>
                        <Text>{t("dashboard.startLearningJourney")}</Text>
                        <Button
                          mt={4}
                          colorScheme="brand"
                          leftIcon={<Icon as={FiPlus} />}
                          onClick={onOpen}
                          size="lg"
                          variant="gradient"
                          boxShadow="lg"
                          _hover={{
                            transform: "translateY(-3px)",
                            boxShadow: "xl",
                          }}
                          transition="all 0.2s"
                        >
                          {t("dashboard.addYourFirstWord")}
                        </Button>
                      </VStack>
                    </Box>
                  )}
                </CardBody>
              </Card>

              <Divider my={6} />

              <HStack
                justify="center"
                mt={4}
                spacing={{ base: 2, md: 4 }}
                flexWrap="wrap"
                w="full"
              >
                <Button
                  as={RouterLink}
                  to="/words"
                  leftIcon={<Icon as={FiList} />}
                  colorScheme="brand"
                  variant="outline"
                  boxShadow="sm"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
                  transition="all 0.2s"
                  size={{ base: "sm", md: "md" }}
                  flex={{ base: "1", sm: "none" }}
                  minW={{ base: "100px", sm: "auto" }}
                >
                  {t("words.viewAllWords")}
                </Button>
                <Button
                  as={RouterLink}
                  to="/analytics"
                  leftIcon={<Icon as={FiBarChart2} />}
                  colorScheme="brand"
                  variant="outline"
                  boxShadow="sm"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
                  transition="all 0.2s"
                  size={{ base: "sm", md: "md" }}
                  flex={{ base: "1", sm: "none" }}
                  minW={{ base: "100px", sm: "auto" }}
                >
                  {t("common.viewAnalytics")}
                </Button>
                <Button
                  as={RouterLink}
                  to="/play"
                  leftIcon={<Icon as={FiZap} />}
                  colorScheme="brand"
                  variant="gradient"
                  boxShadow="md"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                  transition="all 0.2s"
                  size={{ base: "sm", md: "md" }}
                  flex={{ base: "1", sm: "none" }}
                  minW={{ base: "100px", sm: "auto" }}
                >
                  {t("common.startLearning")}
                </Button>
              </HStack>
            </VStack>
          </ScaleFade>
        )}
      </VStack>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: "sm", md: "md" }}
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(5px)" />
        <ModalContent borderRadius="xl" overflow="hidden">
          <Box bg="purple.500" h="8px" w="full" />
          <ModalHeader>{t("words.addWord")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleAddWord}>
              <VStack spacing={4}>
                <Input
                  placeholder={t("words.wordInTargetLang", {
                    language: selectedLanguage,
                  })}
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  boxShadow="sm"
                  _focus={{ boxShadow: "md", borderColor: "purple.400" }}
                  transition="all 0.2s"
                />
                <Select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  placeholder={t("common.selectLanguage")}
                  size="lg"
                  borderRadius="md"
                  boxShadow="sm"
                  _focus={{ boxShadow: "md", borderColor: "purple.400" }}
                  transition="all 0.2s"
                >
                  <option value="english">English</option>
                  <option value="hebrew">Hebrew</option>
                  <option value="dutch">Dutch</option>
                </Select>
                <Button
                  type="submit"
                  colorScheme="brand"
                  width="full"
                  size="lg"
                  isLoading={addWord.isPending || isGeneratingSentences}
                  loadingText={
                    isGeneratingSentences
                      ? t("common.generatingSentences")
                      : t("common.adding")
                  }
                  borderRadius="md"
                  boxShadow="md"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                  transition="all 0.2s"
                  mt={4}
                >
                  {t("words.addWord")}
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={() => {
          if (wordToDelete) {
            deleteWordMutation.mutate(wordToDelete.id);
          }
        }}
        title={t("words.deleteConfirm.title")}
        message={t("words.deleteConfirm.message")}
        confirmText={t("words.deleteConfirm.confirm")}
        cancelText={t("words.deleteConfirm.cancel")}
        isLoading={deleteWordMutation.isPending}
        loadingText={t("common.deleting")}
      />
    </Container>
  );
}